import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import React, {useState} from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium, LabelXSmall} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {staticUrl, toCanonicalUrl, useLocation} from "@khan/urls";

import {couponID, getStripeCheckoutPrice} from "#khanmigo-package/pricing.ts";
import StripeCheckoutRedirect from "#khanmigo-package/stripe-checkout-redirect.tsx";

const CheckoutFailedMini = () => (
    <View style={styles.navLink} data-testid="khanmigo-promo-mini-error">
        <View style={styles.title}>
            <LabelMedium style={styles.titleText}>{t`Oops!`}</LabelMedium>
            <img
                height="30"
                alt=""
                src={staticUrl("/images/guide/purple-khanmigo-winking.png")}
            />
        </View>
        <LabelXSmall style={styles.extendedSubtitle}>
            {t`Something went wrong. This could be caused by a temporary problem. Try again in a few minutes.`}
        </LabelXSmall>
    </View>
);

export const KhanmigoPromoMini = () => {
    const [startCheckout, setStartCheckout] = useState(false);
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const location = useLocation();

    useOnMountEffect(() => {
        publishAnalyticsEvent({
            eventType: "LearnerHomeNavAIGuidePromoViewedTI",
            eventSchemaVersion: 1,
        });
    });

    if (startCheckout) {
        return (
            <StripeCheckoutRedirect
                priceID={getStripeCheckoutPrice()}
                couponID={couponID}
                paymentFrequency="MONTHLY"
                cancelURL={location.href}
                successURL={toCanonicalUrl("/khanmigo/thank-you")}
                renderLoading={() => (
                    <View style={styles.loading}>
                        <CircularSpinner size="small" />
                    </View>
                )}
                renderError={() => <CheckoutFailedMini />}
            />
        );
    }

    return (
        <Clickable
            style={[styles.navLink, styles.clickable]}
            testId="side-nav-promo"
            onClick={() => {
                publishAnalyticsEvent({
                    eventType: "LearnerHomeNavAIGuidePromoClickedTI",
                    eventSchemaVersion: 1,
                });

                return setStartCheckout(true);
            }}
        >
            {() => (
                <View data-testid="khanmigo-promo-mini">
                    <View style={styles.title}>
                        <LabelMedium style={styles.titleText}>
                            {t`Get Khanmigo`}
                        </LabelMedium>
                        <img
                            height="30"
                            alt=""
                            src={staticUrl(
                                "/images/guide/purple-khanmigo-winking.png",
                            )}
                        />
                    </View>
                    <LabelXSmall style={styles.subtitle}>
                        <Trans>
                            Available now for <strong>FREE</strong> for the
                            first 30 days!
                        </Trans>
                    </LabelXSmall>
                </View>
            )}
        </Clickable>
    );
};

const BORDER_SIZE = 1;

const styles = StyleSheet.create({
    navLink: {
        color: color.eggplant,
        borderRadius: 4,
        paddingBottom: spacing.small_12 - BORDER_SIZE,
        paddingLeft: spacing.large_24,
        border: `${BORDER_SIZE}px solid transparent`,
        display: "block",
        textAlign: "left",
    },
    clickable: {
        cursor: "pointer",
        ":focus": {
            borderColor: color.blue,
            outline: 0,
        },
        ":hover": {
            textDecoration: "none",
            color: color.offBlack64,
        },
    },
    title: {
        display: "flex",
        flexDirection: "row",
        alignItems: "end",
        gap: spacing.xxSmall_6,
        marginBottom: spacing.xxxSmall_4,
    },
    titleText: {
        fontWeight: "bold",
    },
    subtitle: {
        maxWidth: 145,
    },
    extendedSubtitle: {
        paddingRight: spacing.large_24,
    },
    loading: {
        alignItems: "center",
        padding: spacing.large_24,
    },
});

export default KhanmigoPromoMini;
